import { useCallback, useEffect, useState } from 'react'

import { getOptimizations } from '@/api/clarityLens'
import { OptimizationsItemModel } from '@/types/clarityLens'

type ScatterChartDataModel = {
  data: {
    name: string
    x: number
    y: number
    info: string
  }[]
}

export default function useOptimizations(
  startDate: string,
  endDate: string,
  branches: string,
  itemName: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [optimizationsChartData, setOptimizationsChartData] =
    useState<ScatterChartDataModel>()

  const fetchAllData = useCallback(async () => {
    if (!itemName) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data } = await fetchOptimizationsData(
        branches,
        startDate,
        endDate,
        itemName
      )

      const scatterChart: ScatterChartDataModel = convertToScatterChart(data)
      setOptimizationsChartData(scatterChart)
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [branches, startDate, endDate, itemName])

  const refetch = useCallback(() => {
    console.log('Refetching sku breakdown information...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    optimizationsChartData,
    loading,
    error,
    refetch,
  }
}

async function fetchOptimizationsData(
  branches: string,
  startDate: string,
  endDate: string,
  itemName: string
): Promise<{
  data: OptimizationsItemModel[]
}> {
  try {
    const response = await getOptimizations(
      branches,
      startDate,
      endDate,
      itemName
    )

    const data: OptimizationsItemModel[] = response.skus.map((sku) => ({
      skuName: sku.sku_name,
      averageUnitPrice: sku.average_unit_price,
      quantity: sku.quantity,
      mfrUom: sku.mfr_uom,
    }))

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

// Convert SKU breakdown data to scatter chart format
function convertToScatterChart(
  skuData: OptimizationsItemModel[]
): ScatterChartDataModel {
  const sortedData = [...skuData].map((sku) => ({
    name: sku.skuName,
    x: sku.averageUnitPrice,
    y: sku.quantity,
    info: sku.mfrUom,
  }))

  return {
    data: sortedData,
  }
}
